import { Button } from '@/components/ui/button';
import { UserPlus, Loader2 } from 'lucide-react';
import { NewInvestorListItem } from './InvestorListItem';
import type { AvailableInvestor } from '../types';

interface NewInvestorsSectionProps {
  availableInvestors: AvailableInvestor[];
  loadingInvestors: boolean;
  onToggleInvestor: (investorId: string) => void;
  onToggleAll: () => void;
}

export function NewInvestorsSection({
  availableInvestors,
  loadingInvestors,
  onToggleInvestor,
  onToggleAll,
}: NewInvestorsSectionProps) {
  if (!loadingInvestors && availableInvestors.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="font-medium">No new investors available</p>
        <p className="text-sm mt-2">
          All fund investors are already included in this drawdown
        </p>
      </div>
    );
  }

  if (availableInvestors.length === 0) {
    return null;
  }

  const allSelected = availableInvestors.every((inv) => inv.isSelected);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">New Investors</h3>
        </div>
        <Button variant="outline" size="sm" onClick={onToggleAll}>
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      <div className="border rounded-lg divide-y max-h-[250px] overflow-y-auto">
        {loadingInvestors ? (
          <div className="p-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading new investors...</span>
          </div>
        ) : (
          availableInvestors.map((investor) => (
            <NewInvestorListItem
              key={investor.investor_id}
              investorId={investor.investor_id}
              investorName={investor.investor_name}
              commitmentAmount={investor.commitment_amount}
              isSelected={investor.isSelected}
              onToggle={onToggleInvestor}
            />
          ))
        )}
      </div>
    </div>
  );
}
